import { makeAutoObservable } from 'mobx';
import { ControllerParams, RootStore } from '@/components/DonationForm/types';
import {
  CurrencyMetrics,
  DonationCampaign,
  DonationCampaignV1,
  FrequencyV1,
  GetDonationCampaignMetricsResponseV1,
} from '@/common/types';
import { ComponentIds as Ids } from '@/components/DonationForm/constants';
import { SPECS } from '@/common/constants/specs';
import { getCurrencyFormatter } from '@/common/utils/currency';
import { getFrequencyLabel } from '@/common/utils/frequenciesToOptions';
import { GoalState } from '@/common/GoalState';
import { hasCampaignGoal } from '@/common/campaign/utils';

export interface FormState {
  frequency: string;
  comment: string;
  amount: number;
  reset(): void;
}

export class RootState {
  public campaign: DonationCampaignV1 | DonationCampaign | undefined;
  public metrics:
    | GetDonationCampaignMetricsResponseV1
    | CurrencyMetrics
    | undefined;
  public error: string | undefined;
  public isPremium: boolean;
  public showPremiumError: boolean;
  public userInputAmount: string | undefined;
  public userInputFrequency: FrequencyV1 | undefined;
  public userInputCoverFees: boolean | undefined;
  public donateButtonLabelBackup: string | null = null;
  public goal = GoalState(
    this,
    this.rootStore.localeKeys,
    this.rootStore.experiments,
    this.rootStore.isV2,
  );

  constructor(
    public readonly rootStore: Omit<RootStore, '$state'>,
    private readonly $props: ControllerParams['$props'],
    private readonly widgetProps: ControllerParams['$widget']['props'],
    private readonly flowAPI: ControllerParams['flowAPI'],
    private readonly controllerConfig: ControllerParams['controllerConfig'],
  ) {
    this.campaign = undefined;
    this.error = undefined;
    this.isPremium = false;
    this.showPremiumError = false;
    this.userInputAmount = undefined;
    this.userInputFrequency = undefined;
    this.userInputCoverFees = undefined;
    this.donateButtonLabelBackup = null;
    makeAutoObservable(this, {
      goal: false,
    });
  }

  public reset(
    campaign?: DonationCampaignV1,
    isViewer: boolean = false,
    isPremium = false,
    metrics?: GetDonationCampaignMetricsResponseV1,
  ) {
    this.campaign = campaign;
    this.isPremium = isPremium;
    this.rootStore.$w(Ids.Amount)?.reset();
    this.rootStore.$w(Ids.Frequency)?.reset();
    this.rootStore.$w(Ids.Comment)?.reset();
    this.error = undefined;
    this.donateButtonLabelBackup = null;
    this.metrics = metrics;

    if (!isViewer && !this.isPremium) {
      this.showPremiumError = true;
    }
  }

  public get currencyFormatter() {
    return getCurrencyFormatter({
      wixCodeApi: this.controllerConfig.wixCodeApi,
      flowAPI: this.flowAPI,
    });
  }

  public get isCoverFeesEnabled(): boolean {
    if (
      this.rootStore.isV2 &&
      this.rootStore.experiments.enabled(SPECS.CoverFeesUoU)
    ) {
      return !!(this.campaign as DonationCampaign)?.coverFeePercentage;
    }
    return false;
  }

  public get isCoverFeeChecked(): boolean {
    return this.userInputCoverFees ?? this.coverFeesDefaultValue;
  }

  public get calculatedTotalAmount(): number | undefined {
    if (!this.selectedAmount) {
      return undefined;
    }
    if (this.isCoverFeesEnabled && this.isCoverFeeChecked) {
      return Number(this.selectedAmount) + this.calculatedFee;
    }
    return Number(this.selectedAmount);
  }

  public get calculatedFee(): number {
    if (this.isCoverFeesEnabled) {
      const { coverFeePercentage } = this.campaign as DonationCampaign;
      const feeRate = Number(coverFeePercentage) / 100;
      const feeValue = feeRate * Number(this.selectedAmount);
      return Number(feeValue.toFixed(2));
    } else {
      return 0;
    }
  }

  public get selectedAmount(): string | undefined {
    return this.userInputAmount ?? this.amountDefaultValue;
  }

  public get formattedTotalAmount(): string | undefined {
    return this.calculatedTotalAmount
      ? this.currencyFormatter(this.calculatedTotalAmount)
      : undefined;
  }

  public get selectedTranslatedFrequency() {
    const selectedFrequency =
      this.userInputFrequency ?? this.frequencyDefaultValue;
    const isSelectedFrequencyOneTime =
      selectedFrequency === FrequencyV1.ONE_TIME;
    return this.formattedTotalAmount && !isSelectedFrequencyOneTime
      ? getFrequencyLabel(selectedFrequency, this.rootStore.localeKeys)
      : undefined;
  }

  public get campaignId() {
    return this.$props.campaignId ?? this.widgetProps.campaignId;
  }

  public get isArchived() {
    if (this.rootStore.isV2) {
      return (this.campaign as DonationCampaign)?.archived;
    }
    return !!(
      (this.campaign as DonationCampaignV1) &&
      (this.campaign as DonationCampaignV1).active === false
    );
  }

  public get coverFeesDefaultValue(): boolean {
    return (
      this.$props.coverFeesDefaultValue ??
      this.widgetProps.coverFeesDefaultValue
    );
  }

  public get amountDefaultValue() {
    const defaultValueFromSettings =
      this.$props.amountDefaultValue ?? this.widgetProps.amountDefaultValue;

    if (this.rootStore.isV2) {
      const amounts = (this.campaign as DonationCampaign)
        ?.predefinedDonationAmounts;
      const doesDefaultValueExists = !!amounts?.find(
        (amount) => amount?.price?.amount === defaultValueFromSettings,
      );

      return doesDefaultValueExists
        ? defaultValueFromSettings
        : amounts?.[0]?.price?.amount || '';
    }

    const amounts = (this.campaign as DonationCampaignV1)?.suggestedAmounts
      ?.amounts;

    const doesDefaultValueExists = !!amounts?.find(
      (amount) => amount?.amount?.amount === defaultValueFromSettings,
    );

    return doesDefaultValueExists
      ? defaultValueFromSettings
      : amounts?.[0]?.amount?.amount || '';
  }

  public get frequencyDefaultValue() {
    const defaultValueFromSettings =
      this.$props.frequencyDefaultValue ??
      this.widgetProps.frequencyDefaultValue;

    if (this.rootStore.isV2) {
      const frequencies = (this.campaign as DonationCampaign)
        ?.donationFrequencies;

      const doesDefaultValueExists = !!frequencies?.find(
        (value) => value === defaultValueFromSettings,
      );

      return doesDefaultValueExists
        ? defaultValueFromSettings
        : frequencies?.[0] || '';
    }

    const frequencies = (this.campaign as DonationCampaignV1)?.recurringDonation
      ?.frequencies;

    const doesDefaultValueExists = !!frequencies?.find(
      (value) => value === defaultValueFromSettings,
    );

    return doesDefaultValueExists
      ? defaultValueFromSettings
      : frequencies?.[0] || '';
  }

  public get isNotFound() {
    return !this.campaign;
  }

  public get shouldShowEmptyState() {
    return this.isArchived || this.isNotFound;
  }

  get isSuggestedAmountEnabled() {
    if (this.rootStore.isV2) {
      return !!(this.campaign as DonationCampaign)?.predefinedDonationAmounts
        ?.length;
    }
    return !!(this.campaign as DonationCampaignV1)?.suggestedAmounts?.enabled;
  }

  get isCustomAmountEnabled() {
    if (this.rootStore.isV2) {
      return !!(this.campaign as DonationCampaign)?.customAmountEnabled;
    }
    return !!(this.campaign as DonationCampaignV1)?.customAmount?.enabled;
  }

  get isFrequencyEnabled() {
    if (this.rootStore.isV2) {
      return !!(this.campaign as DonationCampaign)?.donationFrequencies;
    }
    return !!(this.campaign as DonationCampaignV1)?.recurringDonation?.enabled;
  }

  get isCommentEnabled() {
    if (this.rootStore.isV2) {
      return !!(this.campaign as DonationCampaign)?.commentsEnabled;
    }
    return !!(this.campaign as DonationCampaignV1)?.enableComments;
  }

  get isGoalEnabled() {
    return hasCampaignGoal(this.campaign, this.rootStore.isV2);
  }

  get hasOnlyOneFrequencyOption() {
    if (this.rootStore.isV2) {
      return (
        (this.campaign as DonationCampaign)?.donationFrequencies?.length === 1
      );
    }
    return (
      (this.campaign as DonationCampaignV1)?.recurringDonation?.frequencies
        ?.length === 1
    );
  }

  get shouldDeleteFrequencyWidget() {
    return this.hasOnlyOneFrequencyOption;
  }
}
